<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-row align="center" class="mb-4">
      <v-col cols="12" lg="auto" class="d-flex align-center">
        <report-meals-select-company></report-meals-select-company>
      </v-col>
      <v-col cols="4" lg="auto" class="d-flex align-center">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
      <v-col cols="8" lg="auto" class="d-flex align-center">
        <dropdown
          :label="$t('order_status')"
          :items="statusItems"
          v-model="orderStatus"
          hide-details
        ></dropdown>
      </v-col>
    </v-row>
    <v-data-table
      :headers="getMobileHeaders(headers)"
      :items="customSearch(users, headers, filters.search)"
      class="striped-table ordera-box-shadow"
      dense
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
      :loading="loading"
    >
      <template v-slot:no-data>
        <v-container class="text-center py-12">
          <v-icon size="64" color="grey lighten-1" class="mb-4"> mdi-account-off </v-icon>
          <div class="text-h6 grey--text text--darken-1 mb-2">
            {{ $t("no_employee_report_data") }}
          </div>
          <div class="text-body-2 grey--text">
            {{ $t("select_date_and_filters") }}
          </div>
        </v-container>
      </template>
      <template v-slot:item.num="{ item }">
        {{ item.num }}
      </template>
      <template v-slot:item.id="{ item }">
        <span @click="openUserDialog(item)">{{ item.id }} </span>
      </template>
      <template v-slot:item.shift="{ item }">
        <span v-if="filters.dates.from === filters.dates.to && item.shift">
          {{ formatShift(item.shift) }}
        </span>
      </template>
      <template v-slot:item.date="{ item }">
        <span
          :title="$t('order_datetime')"
          v-if="filters.dates.from === filters.dates.to && item.order_count > 0"
        >
          {{ item.date }}
        </span>
      </template>
      <template v-slot:item.action="{ item }">
        <circle-button
          icon="mdi-format-list-bulleted"
          @click="openDialogReportUser(item)"
          color="primary"
          :tooltip="$t('tooltip_order_details')"
        ></circle-button>
      </template>
      <template v-slot:item.order_count="{ item }">
        {{ item.order_count }}
      </template>
      <template v-slot:item.order_payed="{ item }">
        {{ item.order_payed }}
      </template>
      <template v-slot:item.shift_one="{ item }">
        {{ item.shift_one }}
      </template>
      <template v-slot:item.shift_two="{ item }">
        {{ item.shift_two }}
      </template>
      <template v-slot:item.shift_three="{ item }">
        {{ item.shift_three }}
      </template>
      <template v-slot:item.shift_four="{ item }">
        {{ item.shift_four }}
      </template>
      <template v-slot:body.append>
        <tr class="font-weight-bold totals-row">
          <td v-if="!device.isMobile"></td>
          <td>{{ $t("totalU") }}</td>
          <td></td>
          <td></td>
          <td v-if="filters.dates.from === filters.dates.to"></td>
          <td v-if="filters.dates.from === filters.dates.to"></td>
          <td class="text-center" v-if="selectedShifts.includes(1)">
            {{ getTotalForColumn("shift_one") }}
          </td>
          <td class="text-center" v-if="selectedShifts.includes(2)">
            {{ getTotalForColumn("shift_two") }}
          </td>
          <td class="text-center" v-if="selectedShifts.includes(3)">
            {{ getTotalForColumn("shift_three") }}
          </td>
          <td class="text-center" v-if="selectedShifts.includes(4)">
            {{ getTotalForColumn("shift_four") }}
          </td>
          <td class="text-center">{{ getTotalForColumn("order_count") }}</td>
          <td class="text-center">{{ getTotalForColumn("order_payed") }}</td>
          <td class="text-center">{{ getTotalForColumn("order_difference") }}</td>
          <td class="text-center"></td>
        </tr>
      </template>
    </v-data-table>
    <report-employee-order-history-dialog
      v-model="orderHistoryDialog"
      :employee="userReportItem"
      :company="selectedCompany"
      more-restaurant
    ></report-employee-order-history-dialog>
    <report-employee-dialog
      v-model="showUserDialog"
      :user="selectedUser"
    ></report-employee-dialog>
  </div>
</template>

<script>
import reportService from "@/services/report";
import moment from "moment";
import ReportMealsSelectCompany from "@/components/report/more-restaurants/fields/ReportMealsSelectCompanyField.vue";
import ReportEmployeeOrderHistoryDialog from "@/components/report/employee/dialogs/ReportEmployeeOrderHistoryDialog.vue";
import ReportEmployeeDialog from "@/components/report/employee/dialogs/ReportEmployeeDialog.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";

export default {
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasSnackbar, hasMobileHeaders],
  components: {
    ReportMealsSelectCompany,
    ReportEmployeeOrderHistoryDialog,
    ReportEmployeeDialog,
  },
  data: () => ({
    orderHistoryDialog: false,
    userReportItem: null,
    report: null, // Add report to data
    loading: false,
    selectedUser: null,
    showUserDialog: false,
    selectedShifts: [1, 2, 3], // Po default tri  smene
    orderStatus: 0, // Default: all users
  }),
  methods: {
    formatShift(shift) {
      if (Number.isInteger(shift)) {
        return shift;
      }
      if (typeof shift === "string") {
        return shift.replace("-smena", "");
      }
      return "";
    },
    async getUsers() {
      if (!this.selectedCompany || !this.selectedCompany.id) {
        this.report = null; // Clear report if no company selected
        return;
      }
      this.loading = true;
      this.report = null; // Clear previous report
      const params = {
        date: this.filters.dates.from,
        date2: this.filters.dates.to,
      };
      try {
        const { data } = await reportService.getCompanyMoreRestaurantsKorisnici(
          this.selectedCompany.id,
          params
        );
        this.report = data; // Store the full response in this.report
      } catch (error) {
        this.report = null; // Clear report on error
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },

    openDialogReportUser(item) {
      this.userReportItem = item;
      this.orderHistoryDialog = true;
    },
    openUserDialog(user) {
      this.selectedUser = user;
      this.showUserDialog = true;
    },

    getTotalForColumn(column) {
      if (!this.filteredUsers || this.filteredUsers.length === 0) {
        return 0;
      }

      return this.filteredUsers.reduce((total, user) => {
        const value = user[column] || 0;
        return total + value;
      }, 0);
    },

    createTotalRow() {
      return {
        id: this.$t("totalU"),
        num: "",
        name: "",
        lastname: "",
        date: "",
        shift: "",
        shift_one: this.getTotalForColumn("shift_one"),
        shift_two: this.getTotalForColumn("shift_two"),
        shift_three: this.getTotalForColumn("shift_three"),
        shift_four: this.getTotalForColumn("shift_four"),
        order_count: this.getTotalForColumn("order_count"),
        order_payed: this.getTotalForColumn("order_payed"),
        order_difference: this.getTotalForColumn("order_difference"),
        action: "",
      };
    },
  },
  computed: {
    statusItems() {
      return [
        { text: this.$t("all"), value: 0 },
        { text: this.$t("users_ordered"), value: 1 },
        { text: this.$t("not_ordered"), value: 2 },
        { text: this.$t("users_ordered_not_taken"), value: 3 },
      ];
    },
    selectedCompany() {
      return this.$store.state.report.selectedMealCompany;
    },
    filters() {
      return this.$store.state.report.filters;
    },
    filteredUsers() {
      if (!this.report || !Array.isArray(this.report.users)) {
        return [];
      }

      let filteredUsers = this.report.users;

      // Filter by order status
      if (this.orderStatus !== 0) {
        filteredUsers = filteredUsers.filter((user) => {
          const hasOrders = (user.order_count || 0) > 0;
          const hasTaken = (user.order_payed || 0) > 0;

          switch (this.orderStatus) {
            case 1: // Users who ordered
              return hasOrders;
            case 2: // Users who didn't order
              return !hasOrders;
            case 3: // Users who ordered but didn't take
              return hasOrders && !hasTaken;
            default:
              return true;
          }
        });
      }

      return filteredUsers;
    },
    users() {
      const mappedUsers = this.filteredUsers.map((user, index) => ({
        ...user,
        name: `${this.toLatin(user.name)} ${this.toLatin(user.lastname)}`.trim(),
        num: index + 1,
        date:
          user.date && moment(user.date).isValid()
            ? moment(user.date).format("DD.MM.YYYY HH:mm")
            : "",
        order_difference: (user.order_count || 0) - (user.order_payed || 0),
      }));

      return mappedUsers;
    },
    headers() {
      let h = [
        {
          text: this.$t("r_b"),
          value: "num",
          sortable: false,
          print: false
        },
        { text: "ID", value: "id", sortable: false },
        { text: this.$t("external_code"), value: "external_code" },
        { text: this.$t("fullname"), value: "name", sortable: false },
      ];

      if (this.filters.dates.from === this.filters.dates.to) {
        h.push({
          text: this.$t("date") + "/" + this.$t("time"),
          value: "date",
          sortable: false,
        });
        h.push({
          text: this.$t("shifts"),
          value: "shift",
        });
      }

      h.push();

      // Add shift columns only if they are selected
      const shiftHeaders = [];
      if (this.selectedShifts.includes(1)) {
        shiftHeaders.push({
          text: this.$t("shift_one"),
          value: "shift_one",
          align: "center",
        });
      }
      if (this.selectedShifts.includes(2)) {
        shiftHeaders.push({
          text: this.$t("shift_two"),
          value: "shift_two",
          align: "center",
        });
      }
      if (this.selectedShifts.includes(3)) {
        shiftHeaders.push({
          text: this.$t("shift_three"),
          value: "shift_three",
          align: "center",
        });
      }
      if (this.selectedShifts.includes(4)) {
        shiftHeaders.push({
          text: this.$t("shift_four"),
          value: "shift_four",
          align: "center",
        });
      }

      h.push(...shiftHeaders);

      h.push(
        {
          text: this.$t("ordered"),
          value: "order_count",
          align: "center",
        },
        {
          text: this.$t("taken"),
          value: "order_payed",
          align: "center",
        },
        {
          text: this.$t("difference"),
          value: "order_difference",
          align: "center",
        },
        {
          text: this.$t("report"),
          value: "action",
          sortable: false,
          print: false,
          align: "center",
        }
      );
      return h;
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.getUsers();
      },
      deep: true,
    },
    selectedCompany: {
      handler() {
        this.getUsers();
      },
      immediate: true,
      deep: true,
    },
    selectedShifts: {
      handler() {
        // Data se automatski filtriraju preko computed property
      },
    },
    isPrinting() {
      if (this.isPrinting) {
        const baseItems = this.customSearch(
          this.users,
          this.headers,
          this.filters.search
        );
        // Create new array with total row added
        const printItems = [...baseItems, this.createTotalRow()];

        this.printDocument({
          title: [this.$t("report"), this.$t("employees")].join(": "),
          tables: [
            {
              headers: this.headers,
              items: printItems,
              showCaption: true,
            },
          ],
          hideDatetime: true,
          subtitle: this.subtitle,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        // Prepare items for Excel (remove action column if present)
        const excelHeaders = this.headers.filter((h) => h.value !== "action");
        const baseExcelItems = this.customSearch(
          this.users,
          excelHeaders,
          this.filters.search
        ).map((item) => {
          // Remove the action property if present
          const { action, ...rest } = item;
          return rest;
        });

        // Add total row to Excel items
        const totalRow = this.createTotalRow();
        const { action, ...totalRowForExcel } = totalRow;
        const excelItems = [...baseExcelItems, totalRowForExcel];

        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("employees")].join(" ") + ".xlsx",
          sheets: [
            {
              name: this.$t("employees"),
              headers: excelHeaders,
              items: excelItems,
              title: [this.$t("report"), this.$t("employees")].join(" "),
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
  },
};
</script>
